import React, { useEffect, useState } from "react";
import { fetchFixtureExternalMedia, fetchTeamExternalMedia } from "../api";

const toPosts = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.posts)) return payload.posts;
  return [];
};

const formatPublished = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

function MediaPostCard({ post, dark = false }) {
  const text = dark ? "#f8fafc" : "#111827";
  const muted = dark ? "rgba(255,255,255,0.65)" : "rgba(17,24,39,0.62)";
  const border = dark ? "1px solid rgba(148,163,184,0.18)" : "1px solid rgba(15,23,42,0.1)";
  const bg = dark ? "rgba(255,255,255,0.04)" : "#fff";
  const handle = post?.source_handle ? `@${String(post.source_handle).replace(/^@/, "")}` : "";

  return (
    <article style={{ border, background: bg, borderRadius: 8, padding: 12, color: text }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 9 }}>
        {post?.source_avatar_url ? (
          <img
            src={post.source_avatar_url}
            alt=""
            style={{ width: 36, height: 36, borderRadius: 999, objectFit: "cover", flex: "0 0 auto" }}
            loading="lazy"
          />
        ) : (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: dark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.08)",
              fontWeight: 900,
            }}
          >
            {(post?.source_name || post?.source_handle || "X").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {post?.source_name || handle || "Media source"}{" "}
            {post?.source_verified ? <span style={{ color: "#38bdf8" }}>✓</span> : null}
          </div>
          <div style={{ fontSize: 12, color: muted }}>
            {handle} {post?.published_at ? `• ${formatPublished(post.published_at)}` : ""}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 14, lineHeight: 1.45, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {post?.text || "Media post"}
      </div>

      {post?.media_url ? (
        <img
          src={post.media_url}
          alt=""
          style={{ width: "100%", maxHeight: 260, objectFit: "cover", borderRadius: 8, marginTop: 10 }}
          loading="lazy"
        />
      ) : null}

      {post?.post_url ? (
        <a
          href={post.post_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            marginTop: 10,
            color: dark ? "#86efac" : "#15803d",
            fontSize: 13,
            fontWeight: 900,
            textDecoration: "none",
          }}
        >
          Open on X
        </a>
      ) : null}
    </article>
  );
}

export default function MediaWatchSection({
  mode = "fixture",
  fixtureId,
  teamName,
  title = "Media Watch",
  subtitle = "From trusted and official voices around the game.",
  limit = 8,
  sinceDays = 14,
  dark = false,
  emptyText = "No major media posts matched yet.",
  hideWhenEmpty = false,
}) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const key = mode === "fixture" ? fixtureId : teamName;
    if (!key) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const payload =
          mode === "fixture"
            ? await fetchFixtureExternalMedia(fixtureId, { limit, since_days: sinceDays })
            : await fetchTeamExternalMedia(teamName, { limit, since_days: sinceDays });
        if (!cancelled) setPosts(toPosts(payload).slice(0, limit));
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) {
          setLoaded(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fixtureId, limit, mode, sinceDays, teamName]);

  if (!loaded && !loading) return null;
  if (!loading && loaded && !posts.length && hideWhenEmpty) return null;

  const text = dark ? "#f8fafc" : "#111827";
  const muted = dark ? "rgba(255,255,255,0.66)" : "rgba(17,24,39,0.62)";

  return (
    <section style={{ margin: "18px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", marginBottom: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: text, fontSize: 20 }}>{title}</h2>
          {subtitle ? <div style={{ marginTop: 2, color: muted, fontSize: 13 }}>{subtitle}</div> : null}
        </div>
        {loading ? <span style={{ color: muted, fontSize: 12 }}>Loading...</span> : null}
      </div>

      {!posts.length ? (
        <div style={{ color: muted, fontSize: 13 }}>{emptyText}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
          {posts.map((post, idx) => (
            <MediaPostCard key={post.id || post.external_post_id || idx} post={post} dark={dark} />
          ))}
        </div>
      )}
    </section>
  );
}
